import React, { useState } from 'react';
import { UnitRequirements } from './UnitRequirements';
import { entryMatchesUnit } from '../../lib/helpers';

export function UnitCard({ unit, evidence, signoffs, workplaceSignoffs, knowledgeUnits, apprenticeId, profile, onToggleSignoff, onToggleWorkplace, onToggleKnowledge }) {
  // The toggles report their own failures via toast; swallow the rejection here
  // so a failed tick doesn't surface as an unhandled promise.
  const runToggle = (fn) => (...args) => { Promise.resolve(fn(...args)).catch(() => {}); };
  const [showGuide, setShowGuide] = useState(false);
  const [showWpGuide, setShowWpGuide] = useState(false);
  const defaultTab = unit.photo_guide ? 'photo' : unit.write_guide ? 'write' : unit.example_entry ? 'example' : 'tip';
  const [guideTab, setGuideTab] = useState(defaultTab);
  const apprEvidence = evidence.filter(e => e.apprentice_id === apprenticeId && entryMatchesUnit(e, unit.code));
  const approved = apprEvidence.filter(e => e.status === 'approved').length;
  const pending = apprEvidence.filter(e => e.status === 'pending').length;
  const rejected = apprEvidence.filter(e => e.status === 'rejected').length;
  const minEvidence = unit.min_evidence || 3;

  const es = signoffs.find(s => s.apprentice_id === apprenticeId && s.unit_code === unit.code);
  const ws = workplaceSignoffs.find(s => s.apprentice_id === apprenticeId && s.unit_code === unit.code);
  const ku = knowledgeUnits.find(k => k.apprentice_id === apprenticeId && k.unit_code === unit.code);

  const isTrainer = profile.role === 'trainer';
  const isEmployer = profile.role === 'employer';
  const isSupervisor = profile.role === 'supervisor';
  const isApprentice = profile.role === 'apprentice';

  const kuComplete = !!ku;
  const esComplete = !!es;
  const wsComplete = !!ws;
  // If evidence signoff exists but no evidence in system, treat requirement as met (pre-system evidence)
  const effectiveApproved = (esComplete && approved === 0) ? minEvidence : approved;
  const evidenceMet = effectiveApproved >= minEvidence;

  // Overall status: complete if all three signoffs are done
  const isComplete = kuComplete && esComplete && wsComplete;
  const isInProgress = kuComplete || esComplete || wsComplete || approved > 0;

  // Card state class
  const stateClass = isComplete ? 'unit-complete' : isInProgress ? 'unit-progress' : '';

  return (
    <div className={`unit-card ${stateClass}`}>
      {/* Header */}
      <div className="uc-header">
        <div>
          <div className="uc-code">{unit.code}</div>
          <div className="uc-name">{unit.name}</div>
        </div>
        {isComplete && <span className="uc-badge-done">✓</span>}
      </div>

      {/* Evidence progress */}
      <div className="uc-evidence">
        <div className="uc-ev-label">
          <span>Evidence</span>
          <span className={`uc-ev-count ${evidenceMet ? 'met' : ''}`}>{effectiveApproved}/{minEvidence}</span>
        </div>
        <div className="pbar-wrap">
          <div className="pbar acc" style={{ width: `${Math.min(100, (effectiveApproved / minEvidence) * 100)}%` }} />
        </div>
        {(pending > 0 || rejected > 0) && (
          <div className="uc-ev-sub">
            {pending > 0 && <span className="uc-pending">{pending} pending</span>}
            {rejected > 0 && <span className="uc-rejected">{rejected} returned</span>}
          </div>
        )}
      </div>

      {/* Status pills — order: Knowledge, Evidence, Workplace */}
      <div className="uc-statuses">
        {/* Knowledge */}
        {isApprentice ? (
          <button
            className={`uc-pill ${kuComplete ? 'done' : 'off'}`}
            onClick={runToggle(() => onToggleKnowledge(apprenticeId, unit.code, !kuComplete, profile.id))}
          >
            <span className="uc-pill-icon">{kuComplete ? '✓' : '○'}</span>
            Knowledge
          </button>
        ) : (
          <div className={`uc-pill ${kuComplete ? 'done' : 'off'} readonly`}>
            <span className="uc-pill-icon">{kuComplete ? '✓' : '○'}</span>
            Knowledge
          </div>
        )}

        {/* Evidence signoff */}
        {(isTrainer || isEmployer) ? (
          <button
            className={`uc-pill ${esComplete ? 'done' : 'off'}`}
            onClick={runToggle(() => onToggleSignoff(apprenticeId, unit.code, !esComplete, profile.id))}
          >
            <span className="uc-pill-icon">{esComplete ? '✓' : '○'}</span>
            Evidence
          </button>
        ) : (
          <div className={`uc-pill ${esComplete ? 'done' : 'off'} readonly`}>
            <span className="uc-pill-icon">{esComplete ? '✓' : '○'}</span>
            Evidence
          </div>
        )}

        {/* Workplace */}
        {(isEmployer || isSupervisor) ? (
          <button
            className={`uc-pill ${wsComplete ? 'done' : 'off'}`}
            onClick={runToggle(() => onToggleWorkplace(apprenticeId, unit.code, !wsComplete, profile.id))}
          >
            <span className="uc-pill-icon">{wsComplete ? '✓' : '○'}</span>
            Workplace
          </button>
        ) : (
          <div className={`uc-pill ${wsComplete ? 'done' : 'off'} readonly`}>
            <span className="uc-pill-icon">{wsComplete ? '✓' : '○'}</span>
            Workplace
          </div>
        )}
      </div>

      {/* Workplace Guide — for supervisors and employers */}
      {(isSupervisor || isEmployer) && unit.workplace_guide && (
        <div style={{ marginTop: 6 }}>
          <button
            className="btn-sm"
            style={{ fontSize: 10, padding: '3px 10px', background: wsComplete ? 'rgba(39,174,96,.08)' : 'rgba(245,166,35,.08)', borderColor: wsComplete ? 'var(--green)' : 'var(--accent)', color: wsComplete ? 'var(--green)' : 'var(--accent)' }}
            onClick={() => setShowWpGuide(!showWpGuide)}
          >
            {showWpGuide ? '▾ Hide Workplace Guide' : '▸ Workplace Sign-off Guide'}
          </button>
          {showWpGuide && (
            <div style={{ marginTop: 8, padding: 12, background: 'var(--panel)', borderRadius: 6, border: '1px solid var(--border)', borderLeft: '3px solid var(--accent)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>
                Before signing off, confirm the apprentice can:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {unit.workplace_guide.split('\n').filter(l => l.trim()).map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.5, color: 'var(--text)' }}>
                    <span style={{ color: 'var(--accent)', flexShrink: 0, fontSize: 11 }}>☐</span>
                    <span>{item.trim()}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, fontSize: 10, color: 'var(--muted)', lineHeight: 1.5, fontStyle: 'italic' }}>
                Only sign off when you have directly observed the apprentice demonstrating these skills in the workshop.
              </div>
            </div>
          )}
        </div>
      )}

      <UnitRequirements text={unit.evidence_requirements} />

      {/* Evidence Guide — inline accordion */}
      {(unit.photo_guide || unit.write_guide || unit.example_entry || unit.workshop_tip) && (
        <div style={{ marginTop: 6 }}>
          <button
            className="btn-sm"
            style={{ fontSize: 10, padding: '3px 10px' }}
            onClick={() => setShowGuide(!showGuide)}
          >
            {showGuide ? '▾ Hide Evidence Guide' : '▸ Evidence Guide'}
          </button>
          {showGuide && (
            <div style={{ marginTop: 8, padding: 10, background: 'var(--panel)', borderRadius: 4, border: '1px solid var(--border)' }}>
              {/* Tab buttons */}
              <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 10 }}>
                {[
                  { id: 'photo', label: '📸 What to Photo', show: !!unit.photo_guide },
                  { id: 'write', label: '✏️ What to Write', show: !!unit.write_guide },
                  { id: 'example', label: '📋 Example', show: !!unit.example_entry },
                  { id: 'tip', label: '💡 Workshop Tip', show: !!unit.workshop_tip },
                ].filter(t => t.show).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setGuideTab(t.id)}
                    style={{
                      padding: '6px 10px',
                      fontSize: 10,
                      fontWeight: 600,
                      border: 'none',
                      background: 'none',
                      color: guideTab === t.id ? 'var(--accent)' : 'var(--muted)',
                      borderBottom: guideTab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                      cursor: 'pointer',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {guideTab === 'photo' && unit.photo_guide && (
                <div style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--text)' }}>
                  {unit.photo_guide.split(',').map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                      <span style={{ color: 'var(--accent)', flexShrink: 0 }}>📷</span>
                      <span>{item.trim()}</span>
                    </div>
                  ))}
                  {unit.evidence_count_tip && (
                    <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: '#fffbeb', border: '1px solid #fde68a', fontSize: 11, color: '#92400e' }}>
                      💡 <strong>How many?</strong> {unit.evidence_count_tip}
                    </div>
                  )}
                </div>
              )}

              {guideTab === 'write' && unit.write_guide && (
                <div style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--text)' }}>
                  {unit.write_guide}
                  {unit.min_evidence && (
                    <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 11, color: '#1e40af' }}>
                      📝 <strong>Minimum evidence:</strong> {unit.min_evidence} entries needed
                    </div>
                  )}
                </div>
              )}

              {guideTab === 'example' && unit.example_entry && (
                <div>
                  <div style={{ padding: '10px 12px', borderRadius: 6, background: 'var(--bg2)', border: '1px solid var(--border)', fontSize: 12, lineHeight: 1.8, color: 'var(--text)', fontStyle: 'italic' }}>
                    "{unit.example_entry}"
                  </div>
                  <div style={{ marginTop: 8, fontSize: 10, color: 'var(--muted)', lineHeight: 1.6 }}>
                    ⚡ Example of a good evidence description — describe the vehicle, what you did, what you found, and the result.
                  </div>
                </div>
              )}

              {guideTab === 'tip' && unit.workshop_tip && (
                <div style={{ fontSize: 12, lineHeight: 1.8, color: 'var(--text)' }}>
                  {unit.workshop_tip}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
